import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import CompanyItemsReportPage from '@/pages/ItemsContainer/ItemsCompanyReport';
import CompanyItemsSupplyReportPage from '@/pages/ItemsContainer/ItemsCompanySupplyReport';
import ItemsWantedReport from '@/pages/ItemsContainer/ItemsWantedReport';
import PagePremission from '@/routes/Restrictions/PagePremission';
import { lazyRouteComponent, preloadComponent } from '@/utils/lazyLoad';
import Premission from '@/utils/premissions/premission.type';
import ItemsPremission from '@/utils/premissions/ItemsPremissons.enum';

// Optimized lazy loading with performance enhancements
const ItemGroupPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/ItemsGroup'),
);
const ItemsPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/Items'),
);
const ItemsOrderReportPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/ItemsOrderReport'),
);
const ItemsBatchTracker = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/ItemsBatchTracker'),
);
const IncomeAnalysisPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/IncomeAnalysis'),
);
const SedoFedrenReportPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/SedoFedrenReport'),
);
const ClassQuotaPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/ClassQuota'),
);
const QuotaDetailsPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/ClassQuota/QuotaDetails'),
);
const ItemQuotaPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/ItemQuota'),
);
const ItemQuotaDetailsPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/ItemQuota/ItemQuotaDetails'),
);
const RegionalCompanySalesPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/RegionalCompanySales'),
);
const SalesReturnHistoryIMSPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/SalesReturnHistoryIMS'),
);
const ItemTransactionsPage = lazyRouteComponent(
  () => import('@/pages/ItemsContainer/ItemTransactions'),
);

// Preload the most frequently accessed pages for instant transitions
preloadComponent(() => import('@/pages/ItemsContainer/Items'));

// Define routes with optimized components
const ItemRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="all-items" replace />,
  },
  {
    path: 'items',
    element: <Navigate to="all-items" replace />,
  },
  {
    path: 'all-items',
    element: (
      <PagePremission prem={Premission.Prem_Items_Open_Page}>
        <ItemsPage />
      </PagePremission>
    ),
  },
  {
    path: 'group',
    element: (
      <PagePremission prem={Premission.Prem_GroupItem_Show_Prem}>
        <ItemGroupPage />
      </PagePremission>
    ),
  },
  {
    path: 'items-wanted-report',
    element: (
      <PagePremission prem={Premission.Prem_Items_Shortcomings_Report}>
        <ItemsWantedReport />
      </PagePremission>
    ),
  },
  {
    path: 'companies-report',
    element: (
      <PagePremission prem={Premission.Prem_Items_Companies_Report}>
        <CompanyItemsReportPage />
      </PagePremission>
    ),
  },
  {
    path: 'companies-supply-report',
    element: (
      <PagePremission prem={Premission.Prem_Items_Companies_Report}>
        <CompanyItemsSupplyReportPage />
      </PagePremission>
    ),
  },
  {
    path: 'order-report',
    element: (
      <PagePremission prem={Premission.Prem_Items_Order_Report}>
        <ItemsOrderReportPage />
      </PagePremission>
    ),
  },
  {
    path: 'batch-tracker',
    element: (
      <PagePremission prem={Premission.Prem_Items_Batch_Tracker}>
        <ItemsBatchTracker />
      </PagePremission>
    ),
  },
  {
    path: 'income-analysis',
    element: (
      <PagePremission prem={Premission.Prem_Items_Batch_Tracker}>
        <IncomeAnalysisPage />
      </PagePremission>
    ),
  },
  {
    path: 'sedo-fedren-report',
    element: (
      <PagePremission prem={ItemsPremission.Prem_Items_Sedo_Fedren_Report}>
        <SedoFedrenReportPage />
      </PagePremission>
    ),
  },
  {
    path: 'class-quota',
    element: (
      <PagePremission prem={Premission.Prem_QuotaControl_View}>
        <ClassQuotaPage />
      </PagePremission>
    ),
  },
  {
    path: 'class-quota/details',
    element: (
      <PagePremission prem={Premission.Prem_QuotaControl_View}>
        <QuotaDetailsPage />
      </PagePremission>
    ),
  },
  {
    path: 'item-quota',
    element: (
      <PagePremission prem={Premission.Prem_QuotaControl_View}>
        <ItemQuotaPage />
      </PagePremission>
    ),
  },
  {
    path: 'item-quota/details',
    element: (
      <PagePremission prem={Premission.Prem_QuotaControl_View}>
        <ItemQuotaDetailsPage />
      </PagePremission>
    ),
  },
  {
    path: 'regional-company-sales',
    element: (
      <PagePremission prem={Premission.Prem_Items_Companies_Report}>
        <RegionalCompanySalesPage />
      </PagePremission>
    ),
  },
  {
    path: 'sales-return-history-ims',
    element: (
      <PagePremission prem={Premission.Prem_Items_Companies_Report}>
        <SalesReturnHistoryIMSPage />
      </PagePremission>
    ),
  },
  {
    path: 'item-transactions',
    element: (
      <PagePremission prem={Premission.Prem_Items_Show_Items_Trans}>
        <ItemTransactionsPage />
      </PagePremission>
    ),
  },
];

// Trigger prefetching when user hovers over navigation items (can be called from your nav component)
export const prefetchItemRoutes = () => {
  void import('@/pages/ItemsContainer/ItemsGroup');
  void import('@/pages/ItemsContainer/ItemsOrderReport');
  void import('@/pages/ItemsContainer/SedoFedrenReport');
  void import('@/pages/ItemsContainer/ClassQuota');
  void import('@/pages/ItemsContainer/ClassQuota/QuotaDetails');
  void import('@/pages/ItemsContainer/ItemQuota');
  void import('@/pages/ItemsContainer/ItemQuota/ItemQuotaDetails');
  void import('@/pages/ItemsContainer/RegionalCompanySales');
  void import('@/pages/ItemsContainer/SalesReturnHistoryIMS');
  void import('@/pages/ItemsContainer/ItemTransactions');
};

export default ItemRoutes;
